import React, { useState } from 'react';
import { FIELD_NAME } from '../../../constants/form-constants';
import { STYLED_DOUBLE_SELECT } from '../../../constants/styles-constants';
import { SelectExtended } from './core/select-extended';
import { TextInputExtended } from './core/text-input-extended';

type DocumentType = 'DNI' | 'NIE' | 'NIF' | 'PASAPORTE' | 'OTRO';

interface Props {
	name?: string;
	types?: DocumentType[];
	label?: string;
	required?: boolean;
	disabled?: boolean;
}

export function DniInput(props: Props) {
	const { name = FIELD_NAME.DNI, types: propTypes, label } = props;

	const types = propTypes || ['DNI'];
	const hideTipoDocumento = types.length === 1;
	const [type, setType] = useState<DocumentType>(types[0]);
	const [maxLenght, setMaxLenght] = useState(9);

	const regExpDNI = /^[0-9]{8}[A-Z]$/i;
	const regExpNIE = /^[XYZ][0-9]{7}[A-Z]$/i;
	const regExpNIF = /^[A-Z][0-9]{7}[A-Z0-9]$/i;
	const regExpPASAPORTE = /^[A-Z0-9]{6,9}$/i;
	const lengthOtro = 20;
	const letras = 'TRWAGMYFPDXBNJZSQVHLCKE';

	const checkValidDNILetter = (dni: string) => {
		const regex = /^(\d{8})([A-Z])$/i;
		const match = dni.match(regex);
		if (!match) return false;
		const numero = parseInt(match[1], 10);
		const letraCalculada = letras[numero % 23];
		const letraIngresada = match[2].toUpperCase();
		return letraCalculada === letraIngresada;
	};

	const checkValidNIELetter = (nie: string) => {
		const mapa: Record<'X' | 'Y' | 'Z', string> = { X: '0', Y: '1', Z: '2' };
		const firstLetter = nie[0].toUpperCase() as keyof typeof mapa;
		const numero = mapa[firstLetter] + nie.slice(1, 8);
		const letraCalculada = letras[parseInt(numero) % 23];
		const letraIngresada = nie[8].toUpperCase();
		return letraCalculada === letraIngresada;
	};

	const checkValidNIFLetter = (nif: string) => {
		const letrasNIF = 'JABCDEFGHI';
		const numero = parseInt(nif.slice(1, 8), 10);
		const letraCalculada = letrasNIF[numero % 10];
		const caracterFinal = nif[8].toUpperCase();
		return letraCalculada === caracterFinal || numero.toString() === caracterFinal;
	};

	const validate = (value: string) => {
		if (!getSelectedRegex().test(value)) return false;
		if (type === 'DNI') return checkValidDNILetter(value);
		if (type === 'NIE') return checkValidNIELetter(value);
		if (type === 'NIF') return checkValidNIFLetter(value);
		return true;
	};

	const getSelectedRegex = () => {
		if (type == 'DNI') return regExpDNI;
		if (type == 'NIE') return regExpNIE;
		if (type == 'NIF') return regExpNIF;
		if (type == 'PASAPORTE') return regExpPASAPORTE;
		return new RegExp(`^.{1,${lengthOtro + 1}}$`);
	};

	const getErrorMessage = () => {
		if (type == 'DNI') return 'Ejemplo: 66666666Q';
		if (type == 'NIE') return 'Ejemplo: X6666666Q';
		if (type == 'NIF') return 'Ejemplo: A1234567B';
		if (type == 'PASAPORTE') return 'Ejemplo: ABC123456';
		return 'Error';
	};

	const handleChangeType = (type: string) => {
		setType(type as DocumentType);
		setMaxLenght(type === 'OTRO' ? lengthOtro : 9);
	};

	const getLabel = () => {
		if (label) return label;
		if (types.length === 1) return types[0];
		if (hideTipoDocumento) return type;
		return 'Número de documento';
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'row' }}>
			{!hideTipoDocumento && types.length > 1 && (
				<div style={{ width: STYLED_DOUBLE_SELECT.first.width }}>
					<SelectExtended
						{...props}
						name={FIELD_NAME.DNI_TYPE}
						label={'Tipo'}
						options={types}
						value={type}
						onChange={handleChangeType}
					/>
				</div>
			)}
			<div
				style={{
					width: hideTipoDocumento || types.length === 1 ? '100%' : STYLED_DOUBLE_SELECT.second.width,
					marginLeft: hideTipoDocumento || types.length === 1 ? 0 : STYLED_DOUBLE_SELECT.second.marginLeft,
				}}
			>
				<TextInputExtended
					{...props}
					label={getLabel()}
					name={name}
					validate={validate}
					errorMessage={getErrorMessage()}
					maxLength={maxLenght}
					onChange={(e: any) => {
						e.target.value = e.target.value.toUpperCase();
					}}
				/>
			</div>
		</div>
	);
}
